import React, { useState, useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from "react-router-dom"
import PropTypes from 'prop-types'
import { Card, Filters, ResourceList, ResourceItem, TextStyle, Pagination, Toast } from '@shopify/polaris'
import { useDebounce } from 'use-debounce'
import axios from 'axios'
import moment from 'moment'

import { actionSetSelectedItem, actionSetToastMessage, actionToggleToastActive } from '../store/actions'

import { fetchData, isset } from '../libs/utils'

const PER_PAGE = 50

const ItemListComponent = (props) => {
  const history = useHistory();
  const { resourceName, endpoint, customShortcutActions, onClick } = props
  const [page, setPage] = useState(1)
  const [totalEntries, setTotalEntries] = useState(0)
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])
  const [queryValue, setQueryValue] = useState(null)
  const [queryValueDebounced] = useDebounce(queryValue, 300)
  const dispatch = useDispatch()

  const setSelectedItem = (item) => {
    const action = actionSetSelectedItem(item)
    dispatch(action)
  }

  const handleQueryValueChange = useCallback(
    (value) => setQueryValue(value),
    []
  )
  const handleQueryValueRemove = useCallback(() => setQueryValue(null), [])
  const handleClearAll = useCallback(() => {
    handleQueryValueRemove()
  }, [handleQueryValueRemove])

  const hasNextPagination = () => {
    const totalPages = Math.ceil(totalEntries / PER_PAGE)
    return page < totalPages
  }

  const fetchDataFromAPI = () => {
    fetchData(`${endpoint}?page=${page}&per_page=${PER_PAGE}&search=${queryValueDebounced ? queryValueDebounced : ''}`)
      .then(res => {
        setLoading(false)
        setItems(res.entries)
        setTotalEntries(res.total_entries)
      })
  }

  useEffect(() => {
    setLoading(true)
    fetchDataFromAPI()
  }, [queryValueDebounced, props.forceListRender])

  useEffect(() => {
    setLoading(true)
    fetchDataFromAPI()
  }, [page])

  const filterControl = (
    <Filters
      queryValue={queryValue}
      filters={[]}
      onQueryChange={handleQueryValueChange}
      onQueryClear={handleQueryValueRemove}
      onClearAll={handleClearAll}
    />
  )

  const renderItem = (item) => {
    const { id, name, preset_name, created_at } = item
    const itemName = (name && name.length)
      ? name
      : (preset_name && preset_name.length)
        ? preset_name
        : props.defaultName ? props.defaultName : `Item #${id}`

    const resHandler = (res) => {
      if (res.data.success) {
        fetchDataFromAPI()
      } else {
        setLoading(false)
        const errorMessage = (res.data.errors || []).reduce((message, error) => {
          return message += ` ${error.message}`
        }, '')
        dispatch(actionSetToastMessage(errorMessage))
        dispatch(actionToggleToastActive(true))
      }
    }

    const editAction = isset(props.editAction) ? props.editAction(item) : {
      content: 'Edit',
      url: `/${resourceName.plural}/${id}/edit`,
      onAction: () => {setSelectedItem(item)}
    }

    const duplicateAction = isset(props.duplicateAction) ? props.duplicateAction(item) : {
      content: 'Duplicate',
      onAction: () => {
        setLoading(true)
        axios.post(`/api/v2/admin/${resourceName.plural}/duplicate`, {
          id
        })
          .then(resHandler)
          .catch((e) => {
            console.log(e)
          })
      }
    }

    const getCodeAction = isset(props.getCodeAction) ? props.getCodeAction(item) : {
      content: 'Get code',
      url: `/${resourceName.plural}/${id}/code`,
      onAction: () => {setSelectedItem(item)}
    }

    const deleteAction = isset(props.deleteAction) ? props.deleteAction(item) : {
      content: 'Delete',
      onAction: () => {
        setLoading(true)
        axios.delete(`/api/v2/admin/${resourceName.plural}/delete`, {
          data: { id }
        })
          .then(resHandler)
          .catch((e) => {
            console.log(e)
          })
      }
    }
    const actions = []
    if (editAction) actions.push(editAction)
    if (duplicateAction) actions.push(duplicateAction)
    if (getCodeAction) actions.push(getCodeAction)
    if (deleteAction) actions.push(deleteAction)

    const shortcutActions = customShortcutActions ? customShortcutActions(item) : actions
    const onClickAction = isset(onClick) ?
        typeof onClick === 'function' ?
          () => { onClick(item) } :
          onClick:
        () => {
          setSelectedItem(item)
          history.push(`/${resourceName.plural}/${id}/edit`)
        }
    return (
      <ResourceItem
        id={id}
        onClick={onClickAction}
        accessibilityLabel={`View details for ${itemName}`}
        shortcutActions={shortcutActions}
        persistActions
      >
        <h3>
          <TextStyle variation='strong'>{itemName}</TextStyle>
        </h3>
        <div>
          <TextStyle variation="subdued">
            Created at: {moment(created_at).format('MMM D, YYYY h:mm A')}
          </TextStyle>
        </div>
      </ResourceItem>
    )
  }

  return (
    <Card>
      <ResourceList
        loading={loading}
        resourceName={resourceName}
        items={items}
        renderItem={renderItem}
        filterControl={filterControl}
      />
      {(totalEntries > PER_PAGE) &&
        <div className='polaris-pagination'>
          <Pagination
            hasPrevious={page > 1}
            onPrevious={() => {
              const prevPage = page - 1
              setPage(prevPage)
            }}
            hasNext={hasNextPagination()}
            onNext={() => {
              const nextPage = page + 1
              setPage(nextPage)
            }}
          />
        </div>}
    </Card>
  )
}

ItemListComponent.propTypes = {
  resourceName: PropTypes.object.isRequired,
  endpoint: PropTypes.string.isRequired,
  editAction: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.bool
  ]),
  duplicateAction: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.bool
  ]),
  getCodeAction: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.bool
  ]),
  deleteAction: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.bool
  ])
}

export default ItemListComponent
